<script setup lang="ts">
  import { Modal, useXPathAction } from '@logistiek-zonder-papier/lzp-library'
  import { computed, watch } from 'vue'

  const model = defineModel<string>({ required: true }) // mappingTemplate
  const show = defineModel<boolean>('show', { required: true })
  const props = defineProps<{ listSourcePath: string }>()

  const xpath = useXPathAction()

  const sourceItem = computed<unknown>(() => {
    const raw = props.listSourcePath?.trim() ?? ''
    const path = raw.replace(/^\$\{(.+)\}$/, '$1') // tolerate bare or ${}-wrapped
    const source = path ? xpath.resolvePath(path) : undefined
    return Array.isArray(source) ? source[0] : undefined
  })

  const preview = computed(() =>
    sourceItem.value === undefined ? null : JSON.stringify(sourceItem.value, null, 2)
  )

  function buildMapping(value: unknown, path: string, indent: number): string {
    const pad = '  '.repeat(indent)
    const padInner = '  '.repeat(indent + 1)
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]'
      const items = value.map(
        (item, index) =>
          padInner + buildMapping(item, path ? `${path}.${index}` : `${index}`, indent + 1)
      )
      return `[\n${items.join(',\n')}\n${pad}]`
    }
    if (value !== null && typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>)
      if (entries.length === 0) return '{}'
      const lines = entries.map(
        ([key, item]) =>
          `${padInner}"${key}": ${buildMapping(item, path ? `${path}.${key}` : key, indent + 1)}`
      )
      return `{\n${lines.join(',\n')}\n${pad}}`
    }
    return `\${${path}}` // leaf → bare token
  }

  watch(show, open => {
    if (open && !model.value?.trim() && sourceItem.value !== undefined) {
      model.value = buildMapping(sourceItem.value, '', 0)
    }
  })
</script>

<template>
  <Modal v-model:show="show">
    <template #header>Mapping</template>
    <div class="mapping-split">
      <section class="pane">
        <header class="pane-title">Source (first item)</header>
        <pre v-if="preview" class="json-view">{{ preview }}</pre>
        <div v-else class="json-empty">No preview available</div>
      </section>
      <section class="pane">
        <header class="pane-title">Mapping</header>
        <textarea class="json-editor" v-model="model" spellcheck="false"></textarea>
      </section>
    </div>
  </Modal>
</template>

<style scoped>
  :deep(.modal-container) {
    width: min(1200px, 92vw);
  }
  .mapping-split {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    height: 65vh;
  }
  .pane {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .pane-title {
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--colours-grey-4, #6b7280);
    margin-bottom: 0.5rem;
  }
  .json-view,
  .json-editor {
    flex: 1;
    min-height: 0;
    margin: 0;
    padding: 0.75rem;
    border: 1px solid rgb(209, 213, 219);
    border-radius: 0.375rem;
    font-family: ui-monospace, monospace;
    font-size: 0.8125rem;
    line-height: 1.5;
    white-space: pre;
    overflow: auto;
  }
  .json-view {
    background: var(--colours-grey-2, #f9fafb);
  }
  .json-editor {
    resize: none;
    background: var(--colours-grey-1, #fff);
  }
  .json-empty {
    flex: 1;
    display: grid;
    place-items: center;
    border: 1px dashed rgb(209, 213, 219);
    border-radius: 0.375rem;
    color: var(--colours-grey-4, #6b7280);
    font-size: 0.875rem;
  }
</style>
